import { OrderHistoryParams } from '@diamond/shared/types';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import cleanDeep from 'clean-deep';
import { useEffect, useMemo } from 'react';

import { getOrderHistory } from '../queries';
import { useAuthStore } from '../stores';
import { useShipToStore } from '../stores/useShipToStore';

function orderHistoryQueryOptions(token: string, payload: OrderHistoryParams) {
  return queryOptions({
    queryKey: ['order', 'histories', payload],
    queryFn: () => getOrderHistory(token, payload),
  });
}

function mapPayloadToOrderHistoryParams<T extends OrderHistoryParams>(
  payload: T
): OrderHistoryParams {
  return {
    page: payload['page'],
    size: payload['size'],
    warehouse: payload['warehouse'],
    ...cleanDeep({
      search: payload?.['search'],
      start_date: payload?.['start_date'],
      end_date: payload?.['end_date'],
      group_status: payload?.['group_status'],
      status: payload?.['status'],
      divisi_code: payload?.['divisi_code'],
    }),
  };
}

export function useMyOrderData<
  T extends Omit<OrderHistoryParams, 'page' | 'size' | 'warehouse'>
>(filters: T | undefined) {
  const token = useAuthStore((s) => s.access_token);
  const warehouse = useShipToStore((s) => s.selectedShipToId);
  const queryClient = useQueryClient();

  const params = useMemo(
    () =>
      mapPayloadToOrderHistoryParams({
        page: 1,
        size: 25,
        warehouse,
        ...(filters !== undefined ? filters : {}),
      }),
    [filters, warehouse]
  );

  useEffect(() => {
    const updatedParams = { ...params, page: params.page + 1 };
    queryClient.prefetchQuery(orderHistoryQueryOptions(token, updatedParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page]);

  const {
    data,
    isPending: isLoading,
    refetch,
  } = useQuery(orderHistoryQueryOptions(token, params));

  const pageCount = Number(data?.pagination?.pages);
  const total = Number(data?.pagination?.rows);
  const myOrder = data?.data;

  return {
    myOrder,
    pageCount: !isNaN(pageCount) ? pageCount : 0,
    total: !isNaN(total) ? total : 0,
    isLoading,
    refetch,
  };
}

export default useMyOrderData;
